import { getImage } from "gatsby-plugin-image"; // For optimized Gatsby images
import { BgImage } from "gbimage-bridge";
import React, { useLayoutEffect, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Button from "./Button"; // Custom Button component
import { SafeHtmlParser } from "./SafeHtmlParser"; // Correct import for SafeHtmlParser
import { gsap } from "gsap";

const Header3 = ({ heading, body, backgroundImage, button1, button2 }) => {
	const bgImage = getImage(backgroundImage);
	const headingRef = useRef(null);
	const bodyRef = useRef(null);
	const buttonsRef = useRef(null);

	// Helper function: splits text into word wrappers and animates each word upward.
	// It also removes any HTML tags so that only plain text is animated.
	const animateSplitText = (element, text) => {
		if (!element) return;

		// Hide element initially.
		element.style.visibility = "hidden";
		element.style.opacity = "0";

		// Clear existing content.
		element.textContent = "";

		// Remove any HTML tags (e.g. <p>) from the provided text.
		const plainText = text.replace(/<\/?[^>]+(>|$)/g, "");

		// Build word wrappers.
		const wordWrappers = plainText.split(" ").map((word) => {
			const wrapper = document.createElement("div");
			wrapper.style.overflow = "hidden";
			wrapper.style.display = "inline-block";
			wrapper.style.marginRight = "0.25rem";

			const span = document.createElement("span");
			span.textContent = word;
			span.style.display = "inline-block";
			// Start each word shifted downward.
			span.style.transform = "translateY(100%)";

			wrapper.appendChild(span);
			return wrapper;
		});

		// Append the word wrappers to the element.
		wordWrappers.forEach((wrapper) => element.appendChild(wrapper));

		// Force a reflow (optional) and wait one animation frame.
		void element.offsetHeight;
		requestAnimationFrame(() => {
			// Set a fixed height based on the final layout.
			const finalHeight = element.getBoundingClientRect().height;
			element.style.height = `${finalHeight}px`;

			// Reveal the element.
			element.style.visibility = "visible";
			element.style.opacity = "1";

			// Select all inner spans.
			const spans = element.querySelectorAll("span");

			// Set initial state and animate each span upward.
			gsap.set(spans, { y: "100%" });
			gsap.to(spans, {
				y: "0%",
				duration: 0.6,
				stagger: 0.08,
				ease: "power3.out",
			});
		});
	};

	const animateSplitHeading = (element, text) => {
		if (!element) return;

		// Hide element initially.
		element.style.visibility = "hidden";
		element.style.opacity = "0";

		// Clear existing content.
		element.textContent = "";

		// Remove any HTML tags (e.g. <p>) from the provided text.
		const plainText = text.replace(/<\/?[^>]+(>|$)/g, "");

		// Build word wrappers.
		const wordWrappers = plainText.split(" ").map((word) => {
			const wrapper = document.createElement("div");
			wrapper.style.overflow = "hidden";
			wrapper.style.display = "inline-block";
			wrapper.style.marginRight = "0.6rem";

			const span = document.createElement("span");
			span.textContent = word;
			span.style.display = "inline-block";
			// Start each word shifted downward.
			span.style.transform = "translateY(100%)";

			wrapper.appendChild(span);
			return wrapper;
		});

		// Append the word wrappers to the element.
		wordWrappers.forEach((wrapper) => element.appendChild(wrapper));

		// Force a reflow (optional) and wait one animation frame.
		void element.offsetHeight;
		requestAnimationFrame(() => {
			// Set a fixed height based on the final layout.
			const finalHeight = element.getBoundingClientRect().height;
			element.style.height = `${finalHeight}px`;

			// Reveal the element.
			element.style.visibility = "visible";
			element.style.opacity = "1";

			// Select all inner spans.
			const spans = element.querySelectorAll("span");

			// Set initial state and animate each span upward.
			gsap.set(spans, { y: "100%" });
			gsap.to(spans, {
				y: "0%",
				duration: 0.6,
				stagger: 0.08,
				ease: "power3.out",
			});
		});
	};

	useLayoutEffect(() => {
		// Animate the heading text.
		animateSplitHeading(headingRef.current, heading);
		// Animate the body text.
		animateSplitText(bodyRef.current, body);

		// Animate buttons as whole elements (without splitting).
		if (buttonsRef.current) {
			const btns = buttonsRef.current.children;
			gsap.set(btns, { y: "100%", opacity: 0 });
			gsap.to(btns, {
				y: "0%",
				opacity: 1,
				duration: 0.6,
				stagger: 0.1,
				ease: "power3.out",
				delay: 1,
			});
		}
	}, [heading, body, button1, button2]);

	return (
		<BgImage image={bgImage} className="position-relative py-7 py-lg-7">
			<div
				className="h-100 w-100 position-absolute start-0 top-0"
				style={overlayStyles}
			/>
			<Container style={{ zIndex: 2 }} className="h-100 position-relative">
				<Row className="justify-content-center align-items-end align-items-md-center h-100 text-center">
					<Col className="text-white" lg={8}>
						{/* Animated Heading */}
						<h1 className="display-3" ref={headingRef}>
							{heading}
						</h1>
						{/* Animated Body */}
						<div className="pt-3" ref={bodyRef}>
							<SafeHtmlParser htmlContent={body} />
						</div>
						{/* Animated Buttons */}
						<div
							className="d-flex justify-content-center gap-3 mt-4"
							ref={buttonsRef}
						>
							{button1 && (
								<Button
									colour="#FEDE34"
									text={button1.title}
									link={button1.url}
									isOutline={true}
									outlineTextColor="#fff"
									hoverColor="#000"
								/>
							)}
							{button2 && (
								<Button
									colour="#FEDE34"
									text={button2.title}
									link={button2.url}
									isOutline={false}
								/>
							)}
						</div>
					</Col>
				</Row>
			</Container>
		</BgImage>
	);
};

const overlayStyles = {
	background:
		"linear-gradient(180deg, rgba(0, 18, 33, 0.00) 6.66%, #001221 95.38%)",
};

export default Header3;
